<template>
  <Spinner v-if="loadingStatus" text="Retrieving data..." classes="text-dark-8 mh-100" spinner-color="var(--c-dark-8)"
    spinner-size="15px" />
  <div v-else class="container page-title-area page-content-area">
    <csv-dialog @on-export-csv="fetchAndExportCsv" />

    <header class="title d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-3 mt-5">Analytics</h1>
    </header>

    <section class="text-center mt-5" v-show="isAnalyticsDashboardAllowed">
      <!-- top section with global filters -->
      <div class="global-filters d-flex justify-content-between">
        <div class="filters">
          <!--  -->
          <b-dropdown variant="dark-3" menu-class="dropdown-menu_md" class="m-2" ref="timeDropdown">
            <template #button-content>
              <div class="btn-content">
                <div class="dropdown-label">Show Metrics Between</div>
                <div class="selected-item" v-if="selectedMetricTimeFilter">
                  {{
                    selectedMetricTimeFilter === "custom"
                      ? "Custom"
                      : "Last " + selectedMetricTimeFilter.title
                  }}
                </div>
              </div>
            </template>

            <b-dropdown-item v-for="dateRange in dateRanges" :key="dateRange.id"
              @click="onSelectDateTimeRange(dateRange)">
              <span>Last {{ dateRange.title }}</span>
            </b-dropdown-item>

            <b-dropdown-divider />
            <b-dropdown-form>
              <div class="mx-n3" style="width: 150px">
                <div>
                  <label class="text-dark-7 mb-0 text-s-s">
                    From
                  </label>
                  <VueDatePicker id="start-time-selector" text-input utc enable-seconds v-model="startTime"
                    @update:model-value="onDateSelectionChanged('selectedStartTime')"
                    inputClassName="date-time-picker-input border-0 form-control form-control-md bg-dark-3 text-white font-weight-normal mt-3" />
                </div>
                <div>
                  <label class="text-dark-7 mb-0 text-s-s">
                    To
                  </label>
                  <VueDatePicker id="end-time-selector" text-input utc enable-seconds v-model="endTime"
                    :min-date="startTime || null" :max-date="maxPossibleDate"
                    @update:model-value="onDateSelectionChanged('selectedEndTime')"
                    inputClassName="date-time-picker-input border-0 form-control form-control-md bg-dark-3 text-white font-weight-normal mt-3" />
                </div>
                <b-button variant="primary" size="md" class="mt-3" @click="onSelectDateTimeRange('custom')">
                  Submit dates
                </b-button>
              </div>
            </b-dropdown-form>
          </b-dropdown>

          <b-dropdown variant="dark-3" menu-class="dropdown-menu_md hidden" class="m-2" @toggle="onClickVideoDropdown">
            <template #button-content>
              <div class="btn-content">
                <div class="dropdown-label">Video Titles</div>
                <div class="selected-item">
                  {{
                    selectedStreamIds.length === 1 ? selectedStreamName :
                      selectedStreamIds.length > 1 ? "Selected " + selectedStreamIds.length
                        : "All"
                  }}
                </div>
              </div>
            </template>
          </b-dropdown>
        </div>

        <b-button variant="outline-secondary" size="md" class="mr-2 btn-refresh" @click="refreshData">
          <b-img :src="require('@/assets/images/refresh.svg')" alt="check" class="mr-2" height="20px" />
          Refresh
        </b-button>
      </div>

      <div class="main-content">
        <div class="side-bar">
          <!-- <div class="side-bar" :class="{'resClass': breakdownShow == true}"> -->
          <b-button @click="toggleView('audience')" aria-controls="audience"
            variant="link" :aria-expanded="audienceDropdownExpanded ? 'true' : 'false'"
            class="text-reset text-s-s pl-0 font-weight-normal collapse-button">
            <svg class="transform-none" width="20" height="20" viewBox="0 0 20 20" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="7" r="4" stroke="white" stroke-width="1.5" />
              <path d="M15.5 18C15.5 18 13.9286 14.5 10 14.5C6.07143 14.5 4.5 18 4.5 18" stroke="white"
                stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <span class="mx-2 text-s-s">Audience</span>
            <svg :style="{ transform: audienceDropdownExpanded ? 'rotate(180deg)' : 'none' }" width="16" height="16"
              viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6L8 11L13 6" stroke="white" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </b-button>

          <CollapseContainer ref="collapse-audience" isOpen class="collapsible-item-container" style="margin-top: 10px"
            id="audience">
            <b-button variant="link" class="text-s-ss" :class="{ 'selected-option': selectedOption === 'play' }"
              @click="onSelectOption('play')">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M4.40252 3.12207C3.80411 2.82286 3.10002 3.25801 3.10002 3.92705V12.0729C3.10002 12.742 3.80411 13.1771 4.40252 12.8779L12.5484 8.80498C13.2118 8.47331 13.2118 7.52669 12.5484 7.19501L4.40252 3.12207ZM1.90002 3.92705C1.90002 2.36595 3.54288 1.35061 4.93917 2.04875L13.0851 6.1217C14.6329 6.8956 14.6329 9.10439 13.0851 9.8783L4.93917 13.9512C3.54288 14.6494 1.90002 13.6341 1.90002 12.0729V3.92705Z"
                  fill="#8794B5" />
              </svg>
              Plays
            </b-button>

            <b-button variant="link" class="text-s-ss" :class="{ 'selected-option': selectedOption === 'playAttempts' }"
              @click="onSelectOption('playAttempts')">
              <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M7.1239 1.59883C7.1239 1.26745 7.39253 0.998825 7.7239 0.998825H9.84914C12.3897 0.998825 14.4491 3.05832 14.4491 5.59883V7.60097L15.9753 6.06297C16.2087 5.82775 16.5886 5.82629 16.8238 6.05969C17.059 6.2931 17.0605 6.673 16.8271 6.90822L14.275 9.48007C14.1624 9.5936 14.0091 9.65745 13.8491 9.65745C13.6892 9.65745 13.5359 9.5936 13.4232 9.48007L10.8712 6.90822C10.6378 6.673 10.6392 6.2931 10.8745 6.05969C11.1097 5.82629 11.4896 5.82775 11.723 6.06297L13.2491 7.60097V5.59883C13.2491 3.72106 11.7269 2.19883 9.84914 2.19883H7.7239C7.39253 2.19883 7.1239 1.9302 7.1239 1.59883ZM4.15096 4.3424C4.3109 4.3424 4.46421 4.40626 4.57686 4.51978L7.12905 7.09177C7.36245 7.32698 7.36099 7.70688 7.12577 7.94029C6.89055 8.1737 6.51065 8.17223 6.27725 7.93701L4.75096 6.39888V8.40116C4.75096 10.2789 6.27319 11.8012 8.15096 11.8012H10.2762C10.6076 11.8012 10.8762 12.0698 10.8762 12.4012C10.8762 12.7325 10.6076 13.0012 10.2762 13.0012H8.15096C5.61045 13.0012 3.55096 10.9417 3.55096 8.40116V6.39888L2.02468 7.93701C1.79127 8.17223 1.41138 8.1737 1.17616 7.94029C0.94094 7.70688 0.939472 7.32698 1.17288 7.09177L3.72506 4.51978C3.83771 4.40626 3.99103 4.3424 4.15096 4.3424Z"
                  fill="#8794B5" />
              </svg>
              Play Attempts
            </b-button>

            <b-button variant="link" class="text-s-ss" :class="{ 'selected-option': selectedOption === 'uniqueUsers' }"
              @click="onSelectOption('uniqueUsers')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10 10C11.099 10 12.0506 9.60582 12.8283 8.82809C13.6057 8.05049 14 7.09905 14 5.99987C14 4.90107 13.6058 3.94951 12.8281 3.17166C12.0504 2.39418 11.0989 2 10 2C8.90086 2 7.94945 2.39418 7.17187 3.17178C6.3943 3.94938 6 4.90095 6 5.99987C6 7.09905 6.3943 8.05062 7.17187 8.82822C7.9497 9.60569 8.90124 10 10 10ZM7.86012 3.85993C8.45676 3.26326 9.15667 2.97323 10 2.97323C10.8432 2.97323 11.5432 3.26326 12.14 3.85993C12.7366 4.45671 13.0268 5.15677 13.0268 5.99987C13.0268 6.84323 12.7366 7.54316 12.14 8.13995C11.5432 8.73674 10.8432 9.02677 10 9.02677C9.15692 9.02677 8.45702 8.73661 7.86012 8.13995C7.26335 7.54329 6.9732 6.84323 6.9732 5.99987C6.9732 5.15677 7.26335 4.45671 7.86012 3.85993Z"
                  fill="#8794B5" />
                <path
                  d="M16.9661 14.4245C16.9434 14.123 16.8976 13.794 16.8301 13.4467C16.762 13.0967 16.6743 12.7659 16.5693 12.4636C16.4608 12.151 16.3134 11.8424 16.131 11.5467C15.9419 11.2397 15.7196 10.9724 15.4702 10.7524C15.2094 10.5223 14.8902 10.3373 14.5209 10.2024C14.153 10.0681 13.7452 10.0001 13.3091 10.0001C13.1378 10.0001 12.9721 10.0649 12.6522 10.257C12.4553 10.3754 12.2249 10.5124 11.9679 10.6639C11.748 10.793 11.4503 10.9141 11.0824 11.0236C10.7236 11.1307 10.3592 11.185 9.99949 11.185C9.63999 11.185 9.27564 11.1307 8.91653 11.0236C8.5491 10.9142 8.25118 10.7932 8.03175 10.664C7.77711 10.5139 7.54666 10.377 7.34682 10.2569C7.02714 10.0648 6.86148 10 6.69018 10C6.25387 10 5.84624 10.0681 5.47843 10.2025C5.10946 10.3372 4.79004 10.5222 4.529 10.7525C4.27961 10.9726 4.05736 11.2398 3.86839 11.5467C3.68621 11.8424 3.53873 12.1509 3.43016 12.4637C3.32531 12.766 3.23761 13.0967 3.1695 13.4467C3.10191 13.7936 3.0562 14.1226 3.03354 14.4249C3.01127 14.7204 3 15.0279 3 15.3387C3 16.1465 3.27845 16.8005 3.82755 17.2828C4.36986 17.7587 5.08731 18 5.96006 18H14.0399C14.9124 18 15.6299 17.7587 16.1723 17.2828C16.7215 16.8008 17 16.1466 17 15.3386C16.9999 15.0268 16.9885 14.7192 16.9661 14.4245ZM15.4943 16.6257C15.136 16.9402 14.6602 17.0931 14.0398 17.0931H5.96006C5.33952 17.0931 4.86378 16.9402 4.50557 16.6258C4.15414 16.3173 3.98336 15.8962 3.98336 15.3387C3.98336 15.0487 3.99373 14.7624 4.01447 14.4876C4.03469 14.2179 4.07605 13.9217 4.13737 13.6069C4.19793 13.296 4.275 13.0043 4.36666 12.7402C4.45462 12.4869 4.57457 12.2362 4.72334 11.9946C4.86532 11.7644 5.02868 11.5668 5.20894 11.4077C5.37754 11.2588 5.59007 11.137 5.84048 11.0456C6.07208 10.9611 6.33235 10.9148 6.6149 10.9078C6.64934 10.9247 6.71066 10.9569 6.81001 11.0167C7.01216 11.1382 7.24517 11.2768 7.50275 11.4285C7.79311 11.5992 8.1672 11.7534 8.61413 11.8865C9.07105 12.0227 9.53706 12.0919 9.99962 12.0919C10.4622 12.0919 10.9283 12.0227 11.385 11.8866C11.8323 11.7533 12.2062 11.5992 12.497 11.4282C12.7606 11.2729 12.9871 11.1383 13.1892 11.0167C13.2886 10.957 13.3499 10.9247 13.3843 10.9078C13.667 10.9148 13.9273 10.9611 14.159 11.0456C14.4093 11.137 14.6218 11.2589 14.7904 11.4077C14.9707 11.5667 15.134 11.7643 15.276 11.9947C15.4249 12.2362 15.545 12.487 15.6328 12.7401C15.7246 13.0045 15.8018 13.2962 15.8622 13.6068C15.9234 13.9221 15.9649 14.2185 15.9851 14.4877V14.4879C16.006 14.7617 16.0165 15.0479 16.0166 15.3387C16.0165 15.8963 15.8457 16.3173 15.4943 16.6257Z"
                  fill="#8794B5" />
              </svg>

              Unique Users
            </b-button>

            <b-button variant="link" class="text-s-ss" :class="{
              'selected-option': selectedOption === 'concurrentViewers',
            }" @click="onSelectOption('concurrentViewers')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10 16C6.63099 16 3.40525 13.4437 1.27598 10.7899C0.908007 10.3313 0.908007 9.66548 1.27598 9.20685C1.81133 8.5396 2.93322 7.25229 4.43189 6.12724C8.20659 3.29365 11.7861 3.28819 15.5681 6.12724C17.3288 7.44891 18.724 9.18206 18.724 9.20685C19.092 9.66548 19.092 10.3313 18.724 10.7899C16.5951 13.4434 13.3698 16 10 16ZM10 5.13994C6.26783 5.13994 3.0326 8.8106 2.12997 9.93562C2.10081 9.97199 2.10081 10.0248 2.12997 10.0612C3.03264 11.1862 6.26783 14.8568 10 14.8568C13.7322 14.8568 16.9674 11.1862 17.87 10.0612C17.9226 9.99553 17.8666 9.93562 17.87 9.93562C16.9674 8.8106 13.7322 5.13994 10 5.13994Z"
                  fill="#8794B5" />
                <path
                  d="M10 14C7.79439 14 6 12.2056 6 10C6 7.79439 7.79439 6 10 6C12.2056 6 14 7.79439 14 10C14 12.2056 12.2056 14 10 14ZM10 7.14286C8.42457 7.14286 7.14286 8.42457 7.14286 10C7.14286 11.5754 8.42457 12.8571 10 12.8571C11.5754 12.8571 12.8571 11.5754 12.8571 10C12.8571 8.42457 11.5754 7.14286 10 7.14286Z"
                  fill="#8794B5" />
              </svg>

              Concurrent Viewers
            </b-button>

            <b-button variant="link" class="text-s-ss" :class="{
              'selected-option': selectedOption === 'totalPageLoads',
            }" @click="onSelectOption('totalPageLoads')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5.25 14.3612C6.90685 17.231 10.5764 18.2142 13.4462 16.5574C16.3159 14.9005 17.2992 11.231 15.6423 8.36122C13.9855 5.49146 10.3159 4.50821 7.44615 6.16506L4.84808 7.66506M4.84808 7.66506L5.76314 4.25M4.84808 7.66506L8.26314 8.58013"
                  stroke="#8794B5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>

              Total Page Loads
            </b-button>

            <b-button variant="link" class="text-s-ss" :class="{
              'selected-option': selectedOption === 'totalHoursWatched',
            }" @click="onSelectOption('totalHoursWatched')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M14.9497 5.6435C14.9 5.59382 14.8495 5.54523 14.7986 5.49724L15.2901 4.90787L16.0955 5.57949L16.8553 4.6683L14.3334 2.56519L13.5736 3.47637L14.3789 4.14799L13.8702 4.758C12.8949 4.10821 11.7741 3.71681 10.5932 3.61833V2.40677H9.40678V3.61833C7.76077 3.75557 6.23154 4.46219 5.05026 5.64347C3.72812 6.96558 3 8.72344 3 10.5932C3 12.463 3.72812 14.2208 5.05026 15.543C6.37237 16.8651 8.13023 17.5932 10 17.5932C11.8698 17.5932 13.6276 16.8651 14.9497 15.543C16.2719 14.2208 17 12.463 17 10.5932C17 8.72344 16.2719 6.96558 14.9497 5.6435ZM10 16.4068C6.79439 16.4068 4.18644 13.7988 4.18644 10.5932C4.18644 7.3876 6.79439 4.77965 10 4.77965C13.2056 4.77965 15.8136 7.3876 15.8136 10.5932C15.8136 13.7988 13.2056 16.4068 10 16.4068Z"
                  fill="#8794B5" />
                <path
                  d="M12.4289 6.64886L10.1852 9.42134C10.1249 9.41182 10.063 9.40678 10 9.40678C9.34581 9.40678 8.81357 9.93901 8.81357 10.5932C8.81357 11.2474 9.34581 11.7797 10 11.7797C10.6542 11.7797 11.1864 11.2474 11.1864 10.5932C11.1864 10.4434 11.1582 10.3 11.1073 10.1679L13.3511 7.39523L12.4289 6.64886Z"
                  fill="#8794B5" />
              </svg>

              Total Hours Watched
            </b-button>

            <b-button variant="link" class="text-s-ss" :class="{ 'selected-option': selectedOption === 'viewTime' }"
              @click="onSelectOption('viewTime')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9.93224 10.6091L12.7318 12.7087C12.9381 12.8634 13.2304 12.8217 13.3851 12.6154C13.5397 12.4092 13.498 12.1169 13.2917 11.9622L10.6998 10.0001V6.03407C10.6998 5.77628 10.491 5.56747 10.2332 5.56747C9.9754 5.56747 9.7666 5.77628 9.7666 6.03407V10.2334C9.7666 10.3884 9.83146 10.5279 9.93224 10.6091Z"
                  fill="#8794B5" />
                <path
                  d="M10.4665 3.00119C7.05124 3.00119 4.13478 5.46622 3.56553 8.83365L3.38822 8.57002C3.24381 8.35609 2.95359 8.29963 2.73965 8.44404C2.52572 8.58845 2.46926 8.87868 2.61367 9.09261L3.54686 10.4924C3.62362 10.609 3.74867 10.6853 3.88748 10.7H3.93414C4.05779 10.6996 4.17607 10.6501 4.26309 10.5624L5.42958 9.3959C5.61179 9.21369 5.61179 8.91787 5.42958 8.73566C5.24738 8.55346 4.95155 8.55346 4.76935 8.73566L4.48006 9.02729C5.01734 5.72052 8.13351 3.47572 11.44 4.01301C14.7466 4.55029 16.9918 7.66622 16.4546 10.973C15.9775 13.9093 13.4413 16.0664 10.4665 16.0659C8.59031 16.1 6.81584 15.2148 5.71421 13.6956C5.56466 13.4856 5.27327 13.4364 5.0633 13.5859C4.85333 13.7355 4.80411 14.0269 4.95365 14.2368C6.22933 16.0034 8.28795 17.0348 10.4665 16.9991C14.332 16.9991 17.4654 13.8657 17.4654 10.0001C17.4654 6.13462 14.332 3.00119 10.4665 3.00119Z"
                  fill="#8794B5" />
              </svg>

              View Time
            </b-button>
          </CollapseContainer>

          <hr />

          <b-button @click="toggleView('resolution')" aria-controls="resolution"
            variant="link" :aria-expanded="resolutionDropdownExpanded ? 'true' : 'false'"
            class="text-reset text-s-s pl-0 font-weight-normal collapse-button">
            <svg class="transform-none" width="20" height="20" viewBox="0 0 20 20" fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15 17H15.3C16.4201 17 16.9802 17 17.408 16.782C17.7843 16.5903 18.0903 16.2843 18.282 15.908C18.5 15.4802 18.5 14.9201 18.5 13.8V7.8C18.5 6.11984 18.5 5.27976 18.173 4.63803C17.8854 4.07354 17.4265 3.6146 16.862 3.32698C16.2202 3 15.3802 3 13.7 3H4.7C3.57989 3 3.01984 3 2.59202 3.21799C2.21569 3.40973 1.90973 3.71569 1.71799 4.09202C1.5 4.51984 1.5 5.07989 1.5 6.2V6.5M8.8 9.5H4.7C3.5799 9.5 3.01984 9.5 2.59202 9.71799C2.21569 9.90973 1.90973 10.2157 1.71799 10.592C1.5 11.0198 1.5 11.5799 1.5 12.7V13.8C1.5 14.9201 1.5 15.4802 1.71799 15.908C1.90973 16.2843 2.21569 16.5903 2.59202 16.782C3.01984 17 3.57989 17 4.7 17H8.8C9.9201 17 10.4802 17 10.908 16.782C11.2843 16.5903 11.5903 16.2843 11.782 15.908C12 15.4802 12 14.9201 12 13.8V12.7C12 11.5799 12 11.0198 11.782 10.592C11.5903 10.2157 11.2843 9.90973 10.908 9.71799C10.4802 9.5 9.92011 9.5 8.8 9.5Z"
                stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>

            <span class="mx-2 text-s-s">Resolution</span>
            <svg :style="{ transform: resolutionDropdownExpanded ? 'rotate(180deg)' : 'none' }" width="16" height="16"
              viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6L8 11L13 6" stroke="white" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </b-button>

          <CollapseContainer ref="collapse-resolution" class="collapsible-item-container" id="resolution">
            <b-button variant="link" class="text-s-ss" :class="{ 'selected-option': selectedOption === 'landscape' }"
              @click="onSelectOption('landscape')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 8V4H5" stroke="#8794B5" stroke-width="1.5" />
                <path d="M1 12V16H5" stroke="#8794B5" stroke-width="1.5" />
                <path d="M15 4L19 4L19 8" stroke="#8794B5" stroke-width="1.5" />
                <path d="M15 16L19 16L19 12" stroke="#8794B5" stroke-width="1.5" />
                <rect x="3.75" y="6.75" width="12.5" height="6.5" stroke="#8794B5" stroke-width="1.5" />
              </svg>

              Landscape
            </b-button>

            <b-button variant="link" class="text-s-ss" :class="{ 'selected-option': selectedOption === 'portrait' }"
              @click="onSelectOption('portrait')">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 5V1H8" stroke="#8794B5" stroke-width="1.5" />
                <path d="M4 15V19H8" stroke="#8794B5" stroke-width="1.5" />
                <path d="M12 1L16 1L16 5" stroke="#8794B5" stroke-width="1.5" />
                <path d="M12 19L16 19L16 15" stroke="#8794B5" stroke-width="1.5" />
                <rect x="6.75" y="3.75" width="6.5" height="12.5" stroke="#8794B5" stroke-width="1.5" />
              </svg>

              Portrait
            </b-button>
          </CollapseContainer>
        </div>

        <div class="content-area">
          <Spinner v-if="showLocalLoader" text="Retrieving data..." classes="text-dark-8 local-loader"
            spinner-color="var(--c-dark-8)" spinner-size="15px" />

          <div class="chart-container" v-show="!showLocalLoader">
            <div class="selected-option-header flex items-center" :key="chartRefreshCounter">
              {{ optionMap[selectedOption] }}
              <svg id="selected-option-tooltip" class="ml-2"
                v-show="!['landscape', 'portrait'].includes(selectedOption)" width="18" height="18" viewBox="0 0 18 18"
                fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M17.5 9C17.5 13.6944 13.6944 17.5 9 17.5C4.30558 17.5 0.5 13.6944 0.5 9C0.5 4.30558 4.30558 0.5 9 0.5C13.6944 0.5 17.5 4.30558 17.5 9ZM10.25 5C10.25 5.69036 9.69036 6.25 9 6.25C8.30964 6.25 7.75 5.69036 7.75 5C7.75 4.30964 8.30964 3.75 9 3.75C9.69036 3.75 10.25 4.30964 10.25 5ZM9.85 9C9.85 8.53056 9.46944 8.15 9 8.15C8.53056 8.15 8.15 8.53056 8.15 9V13.5C8.15 13.9694 8.53056 14.35 9 14.35C9.46944 14.35 9.85 13.9694 9.85 13.5V9Z"
                  fill="#8794B5" />
              </svg>
              <b-tooltip target="selected-option-tooltip" placement="top" custom-class="tooltip-container">
                <div class="tooltip-content" v-if="selectedOption === 'play'">
                  <h5>
                    What are Plays?
                  </h5>
                  <p>
                    Plays are a measure of your audience that started a video
                    playback session.
                  </p>

                  <h5>Good to know:</h5>
                  <ol>
                    <li>
                      Plays are not unique users. If a user watches multiple
                      videos, the user will be counted as multiple plays.
                    </li>
                    <li>
                      If a user re-plays a video after it has already played to
                      the end, the user will generate a new play.
                    </li>
                  </ol>

                  <h5>Why is this helpful?</h5>
                  <ol>
                    <li>Find out where your users are coming from.</li>
                    <li>Find out what browsers to optimize your site for.</li>
                  </ol>
                </div>
                <div class="tooltip-content" v-else-if="selectedOption === 'playAttempts'">
                  <h5>
                    What are Play Attempts?
                  </h5>

                  <p>
                    Play attempts count the number of viewers that have pressed
                    the play button to attempt to play a video.
                  </p>

                  <h5>Good to know:</h5>
                  <ol>
                    <li>
                      Look at the difference between plays and play attempts to
                      assess how many viewers pressed play but never saw a frame
                      of the video.
                    </li>
                  </ol>
                </div>
                <div class="tooltip-content" v-if="selectedOption === 'uniqueUsers'">
                  <h5>
                    What are Unique Users?
                  </h5>

                  <p>
                    Unique users represent a unique browser that started a video
                    playback session at least once.
                  </p>

                  <h5>Good to know:</h5>
                  <ol>
                    <li>
                      It doesn't matter how often a user plays a video - the
                      user is only counted once.
                    </li>
                    <li>
                      On the web unique users are tracked via a cookie stored in
                      the client's browser. On native mobile platforms we track
                      the Device ID.
                    </li>
                    <li>
                      A user might visit the site with another browser or clear
                      his cookies. In that case the user will be a new unique
                      user.
                    </li>
                  </ol>

                  <h5>Why is this helpful?</h5>
                  <ol>
                    <li>Find out where your users are coming from.</li>
                    <li>Find out what browsers to optimize your site for.</li>
                  </ol>
                </div>
                <div class="tooltip-content" v-else-if="selectedOption === 'concurrentViewers'">
                  <h5>
                    What are Concurrent Viewers?
                  </h5>
                  <p>
                    Similar to impressions, concurrent viewers are a measure of
                    how many streams are actively being watched. The main
                    difference to impressions is that impressions are the total
                    number of started streams during an hour, while concurrent
                    viewers are the maximum number of people watching at the
                    same time.
                  </p>
                  <br />
                  <p>
                    This means that if you have 10 people joining a stream per
                    minute and 10 people leaving you'd end up with 600
                    impressions during that hour but only 10 concurrent viewers
                  </p>

                  <h5>Good to know:</h5>
                  <ol>
                    <li>
                      Concurrent viewers are not unique users. If a user watches
                      multiple videos the same user will be counted as multiple
                      viewers.
                    </li>
                    <li>
                      Concurrent viewers have a minute resolution so two users
                      are considered concurrent if they are viewing the stream
                      within the same minute.
                    </li>
                  </ol>
                </div>
                <div class="tooltip-content" v-else-if="selectedOption === 'totalPageLoads'">
                  <h5>What are Total Page Loads?</h5>
                  <p>
                    Total page loads are the number of times a player was
                    loaded. A number which is usually much higher than the
                    amount of people that actually initiated a play session.
                  </p>
                  <p>
                    In constrast to impressions and unique users this metric
                    gives you an unfiltered view into all your player sessions.
                  </p>
                </div>
                <div class="tooltip-content" v-else-if="selectedOption === 'totalHoursWatched'">
                  <h5>What are Total Hours Watched?</h5>
                  <p>
                    Total hours watched is the summed up playtime of all
                    impressions.
                  </p>
                </div>
                <div class="tooltip-content" v-else-if="selectedOption === 'viewTime'">
                  <h5>What are View Time?</h5>
                  <p>
                    How long do your users spend with one video on average.
                  </p>
                </div>
              </b-tooltip>
            </div>
            <!-- <div id="chart-container" v-if="allData.length"> -->
            <ejs-chart :key="chartRefreshCounter"
              v-if="allData.length && ['landscape', 'portrait'].includes(this.selectedOption)" id='chart-container'
              theme="Material" :primaryXAxis='primaryXAxisCategory' :primaryYAxis='primaryYAxis'
              :legendSettings='legend'>
              <e-series-collection>
                <e-series :dataSource='categoryData' type='Bar' xName='0' yName='1' :name='optionMap[selectedOption]'>
                </e-series>
              </e-series-collection>
            </ejs-chart>
            <ejs-chart :key="chartRefreshCounter + 'd'" v-else-if="allData.length" id='chart-container' theme="Material"
              :zoomSettings='zoom' @zoomComplete='onZoomComplete' :title='title' :primaryXAxis='primaryXAxis' :primaryYAxis='primaryYAxis'
              :axisLabelRender='axisLabelRender' :chartArea='chartArea' :legendSettings='legend' :tooltip='tooltip' 
              :tooltipRender="onTooltipRender">
              <e-series-collection>
                <e-series :dataSource='allData' type='SplineArea' xName='0' yName='1' :name='optionMap[selectedOption]'
                  width=2 opacity=0.5 :border='border' :marker='circleMarker'> </e-series>
              </e-series-collection>
            </ejs-chart>
            <!-- </div> -->
            <div class="no-data" v-else-if="!allData.length">
              <svg width="41" height="40" viewBox="0 0 41 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M28.8875 9.11252L21.3875 1.61252C21.2707 1.49667 21.1322 1.40501 20.9799 1.34281C20.8276 1.2806 20.6645 1.24907 20.5 1.25002H4.25C3.58696 1.25002 2.95107 1.51341 2.48223 1.98225C2.01339 2.45109 1.75 3.08698 1.75 3.75002V36.25C1.75 36.9131 2.01339 37.5489 2.48223 38.0178C2.95107 38.4866 3.58696 38.75 4.25 38.75H26.75C27.413 38.75 28.0489 38.4866 28.5178 38.0178C28.9866 37.5489 29.25 36.9131 29.25 36.25V10C29.251 9.83551 29.2194 9.67243 29.1572 9.52014C29.095 9.36784 29.0034 9.22932 28.8875 9.11252Z"
                  fill="#A1B2E8" fill-opacity="0.14" />
                <path
                  d="M21.3875 1.61252C21.2707 1.49667 21.1322 1.40501 20.9799 1.34281C20.8276 1.2806 20.6645 1.24907 20.5 1.25002H19.25V9.53752C19.25 9.76241 19.2943 9.9851 19.3804 10.1929C19.4664 10.4006 19.5926 10.5894 19.7516 10.7484C19.9106 10.9075 20.0994 11.0336 20.3072 11.1197C20.5149 11.2057 20.7376 11.25 20.9625 11.25H29.25V10C29.251 9.83551 29.2194 9.67243 29.1572 9.52014C29.095 9.36784 29.0034 9.22932 28.8875 9.11252L21.3875 1.61252Z"
                  fill="#A7B5E2" fill-opacity="0.25" />
                <path
                  d="M29.25 38.75C34.7728 38.75 39.25 34.2728 39.25 28.75C39.25 23.2272 34.7728 18.75 29.25 18.75C23.7272 18.75 19.25 23.2272 19.25 28.75C19.25 34.2728 23.7272 38.75 29.25 38.75Z"
                  fill="#E25858" />
                <path
                  d="M31.0125 28.7502L33.8875 25.8877C34.004 25.7712 34.0965 25.6328 34.1596 25.4805C34.2226 25.3283 34.2551 25.1651 34.2551 25.0002C34.2551 24.8354 34.2226 24.6722 34.1596 24.5199C34.0965 24.3676 34.004 24.2293 33.8875 24.1127C33.7709 23.9962 33.6326 23.9037 33.4803 23.8407C33.328 23.7776 33.1648 23.7451 33 23.7451C32.8352 23.7451 32.672 23.7776 32.5197 23.8407C32.3674 23.9037 32.229 23.9962 32.1125 24.1127L29.25 26.9877L26.3875 24.1127C26.2709 23.9962 26.1326 23.9037 25.9803 23.8407C25.828 23.7776 25.6648 23.7451 25.5 23.7451C25.3352 23.7451 25.172 23.7776 25.0197 23.8407C24.8674 23.9037 24.729 23.9962 24.6125 24.1127C24.4959 24.2293 24.4035 24.3676 24.3404 24.5199C24.2773 24.6722 24.2449 24.8354 24.2449 25.0002C24.2449 25.1651 24.2773 25.3283 24.3404 25.4805C24.4035 25.6328 24.4959 25.7712 24.6125 25.8877L27.4875 28.7502L24.6125 31.6127C24.4953 31.7289 24.4023 31.8672 24.3389 32.0195C24.2754 32.1718 24.2427 32.3352 24.2427 32.5002C24.2427 32.6652 24.2754 32.8286 24.3389 32.981C24.4023 33.1333 24.4953 33.2715 24.6125 33.3877C24.7287 33.5049 24.8669 33.5979 25.0193 33.6613C25.1716 33.7248 25.335 33.7575 25.5 33.7575C25.665 33.7575 25.8284 33.7248 25.9807 33.6613C26.133 33.5979 26.2713 33.5049 26.3875 33.3877L29.25 30.5127L32.1125 33.3877C32.2287 33.5049 32.3669 33.5979 32.5193 33.6613C32.6716 33.7248 32.835 33.7575 33 33.7575C33.165 33.7575 33.3284 33.7248 33.4807 33.6613C33.633 33.5979 33.7713 33.5049 33.8875 33.3877C34.0046 33.2715 34.0976 33.1333 34.1611 32.981C34.2246 32.8286 34.2572 32.6652 34.2572 32.5002C34.2572 32.3352 34.2246 32.1718 34.1611 32.0195C34.0976 31.8672 34.0046 31.7289 33.8875 31.6127L31.0125 28.7502Z"
                  fill="white" />
              </svg>
              <div class="main-err-msg">
                There is currently no data available to display.
              </div>
              <div class="secondary-err-msg">
                Try changing range or
                <a href="" @click.prevent="refreshData">refresh</a>
              </div>
              <div class="secondary-err-msg" style="max-width: 450px">
                This report will display analytics exclusively for players. It does not capture data from streams to
                social
                media platforms
              </div>
            </div>
          </div>

          <b-row class="break-down-table mt-0 pt-4" :class="{ 'show-level': breakdownShow == true }" v-show="!['landscape', 'portrait'].includes(this.selectedOption) &&
            !showLocalLoader
            ">
            <b-col class="break-down-filters" cols="3">
              <h3 class="text-s-xxl">Breakdown</h3>
              <b-form-group v-slot="{ ariaDescribedby }">
                <b-form-radio-group v-model="selectedBreakdown" @input="onSelectBreakdown" :options="options"
                  :aria-describedby="ariaDescribedby" class="breakdown-options" name="breakdown-option"
                  stacked></b-form-radio-group>
              </b-form-group>
            </b-col>

            <b-col class="break-down-results p-0">
              <div class="title w-100">
                <h3 class="text-s-xxl" v-if="selectedBreakdown === 'all'">
                  <svg class="res-block mr-2" @click="changeBreakdown" xmlns="http://www.w3.org/2000/svg" width="8"
                    height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M7 13L1 7L7 1" stroke="white" stroke-width="1.7" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                  {{ optionMap[selectedOption] }}
                </h3>
                <h3 class="text-s-xxl" v-else>
                  <svg class="res-block mr-2" @click="changeBreakdown" xmlns="http://www.w3.org/2000/svg" width="8"
                    height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M7 13L1 7L7 1" stroke="white" stroke-width="1.7" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                  Breakdown by: {{ breakDownOptionMap[selectedBreakdown] }}
                </h3>

                <b-button variant="outline-secondary" size="md" class="mr-2 export-csv-btn"
                  v-b-modal.modal-analytics-csv-export :disabled="showBreakdownLoader">
                  <b-img :src="require('@/assets/images/copy.svg')" alt="check" class="mr-2" height="20px" />
                  Export .CSV
                </b-button>
              </div>

              <div v-if="!showBreakdownLoader && selectedBreakdown === 'all'" class="w-100">
                <div class="w-100 d-flex justify-content-between break-down-results-header px-3">
                  <div class="text-uppercase">Date</div>
                  <div class="text-uppercase">
                    {{ optionMap[selectedOption] }}
                  </div>
                </div>
                <div v-for="(point, index) in allDataReversed" :key="index"
                  class="w-100 d-flex justify-content-between px-3 py-2">
                  <div>{{ getDate(point[0]) }}</div>
                  <div>{{ formatTime(point[1]) }}</div>
                </div>
              </div>
              <div v-else-if="!showBreakdownLoader" class="w-100">
                <div class="w-100 d-flex justify-content-between break-down-results-header px-3">
                  <div class="text-uppercase">
                    {{ breakDownOptionMap[selectedBreakdown] }}
                  </div>
                  <div class="text-uppercase">
                    {{ optionMap[selectedOption] }}
                  </div>
                </div>

                <b-checkbox-group v-model="selectedBreakdownOptions" @change="onSelectBreakdownOption">
                  <div v-if="breakdownData.length > 0" class="w-100 d-flex justify-content-between px-3 py-2">
                    <div><b-checkbox value="total">Total</b-checkbox></div>
                    <div>{{ formatTime(totalBreakDownValue) }}</div>
                  </div>
                  <div v-for="point in breakdownData" :key="point[0]"
                    class="w-100 d-flex justify-content-between px-3 py-2">
                    <div>
                      <b-checkbox :disabled="selectedBreakdownOptions.length >= 5 &&
                        !selectedBreakdownOptions.includes(point[0])
                        " :value="point[0]">{{ point[0] }}</b-checkbox>
                    </div>
                    <div>{{ formatTime(point[1]) }}</div>
                  </div>
                </b-checkbox-group>
              </div>
              <Spinner v-else text="Retrieving data..." classes="text-dark-8 local-breakdown-loader"
                spinner-color="var(--c-dark-8)" spinner-size="15px" />
            </b-col>
          </b-row>
        </div>
      </div>
    </section>

    <section v-if="!isAnalyticsDashboardAllowed"
      class="text-center h-100 d-flex justify-content-center align-items-center">
      <div class="not-available">
        <div>Analytics is available for All in One Plans only</div>
        <b-button size="md" class="mt-5" variant="primary" to="/subscribe">
          Upgrade Plan
        </b-button>
      </div>
    </section>

    <analytics-select-stream-modal ref="analyticsSelectStreamsModal" />
  </div>
</template>

<script>
import _ from 'lodash';
import Spinner from "@/components/ui/Spinner.vue";
import AnalyticsSelectStreamModal from "@/components/modals/AnalyticsSelectStreamModal";
import MetricsService from "@/services/MetricsService";
import IntegrationService from "@/services/IntegrationService";
import { mapGetters } from "vuex";
import Highcharts from "highcharts";
import moment from "moment";
import csvDownload from "json-to-csv-export";
import { Duration } from "luxon";
import StreamService from '../../services/StreamService.js';
import CsvDialog from './CsvDialog.vue';
import { ChartComponent, SeriesDirective, SeriesCollectionDirective, SplineAreaSeries, Legend, DateTime, Highlight, Tooltip, Zoom, BarSeries, Category } from "@syncfusion/ej2-vue-charts";
import CollapseContainer from '@/components/Atoms/CollapseContainer.vue'

Highcharts.setOptions({
  time: {
    timezoneOffset: new Date().getTimezoneOffset(),
  },
});

export default {
  name: "AnalyticsDashboard",
  components: {
    Spinner,
    AnalyticsSelectStreamModal,
    CsvDialog,
    'ejs-chart': ChartComponent,
    'e-series-collection': SeriesCollectionDirective,
    'e-series': SeriesDirective,
    CollapseContainer,
  },
  data() {
    return {
      chartRefreshCounter: 0,
      showLocalLoader: false,
      showBreakdownLoader: false,
      audienceDropdownExpanded: true,
      resolutionDropdownExpanded: false,
      selectedOption: "play",
      selectedMetricTimeFilter: { id: 3, title: "3 hours", time: 180 },
      allData: [],
      allDataReversed: [],
      categoryData: [],
      breakdownData: [],
      totalBreakDownValue: 0,
      dateRanges: [
        { id: 1, title: "15 minutes", time: 15 },
        { id: 2, title: "hour", time: 60 },
        { id: 3, title: "3 hours", time: 180 },
        { id: 4, title: "24 hours", time: 1440 },
        { id: 5, title: "three days", time: 4320 },
        { id: 6, title: "seven days", time: 10080 },
        { id: 7, title: "30 days", time: 43200 },
      ],
      selectedFilterGroups: [],
      selectedVideoTitle: "",
      selectedStreamIds: [],
      selectedVideoFiles: [],
      selectedBreakdown: "all",
      rawDataAfterBreakdown: [],
      cachedBreakdownOptionData: {},
      options: [
        { text: "All Data", value: "all" },
        { text: "Browser", value: "browser" },
        { text: "Country", value: "country" },
        { text: "Device Class", value: "device_class" },
        { text: "Operating System", value: "operatingSystem" },
      ],
      selectedBreakdownOptions: [],
      countryList: {},
      startTime: null,
      endTime: null,
      maxPossibleDate: new Date().toISOString(),
      breakdownShow: false,
      //Initializing Primary X Axis
      primaryXAxis: {
        minimum: null,
        maximum: null,
        valueType: 'DateTime',
        intervalType: 'Auto',
        minorGridLines: { width: 0 },
        majorGridLines: { width: 0 },
        labelStyle: {
          size: '12px', color: '#8794B5',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
        },
      },
      primaryXAxisCategory: {
        valueType: 'Category',
        minorGridLines: { width: 0 },
        majorGridLines: { width: 0 },
        labelStyle: {
          size: '12px', color: '#8794B5',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
        },
      },
      zoom:
      {
        enableMouseWheelZooming: false,
        enableSelectionZooming: true,
        enablePinchZooming: true,
        enablePan: false,
        showToolbar: true,
        mode: 'X'
      },
      //Initializing Primary Y Axis
      primaryYAxis: {
        title: '',
        lineStyle: { width: 0 },
        minorGridLines: { width: 0 },
        majorGridLines: { width: 1 },
        labelStyle: {
          size: '12px', color: '#8794B5',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
        },
      },
      chartArea: {
        border: {
          width: 0
        }
      },
      border: {
        width: 2
      },
      circleMarker: { visible: true, height: 6, width: 6, shape: 'Circle', isFilled: true },
      width: '100%',
      legend: {
        enableHighlight: true,
        textStyle: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
        }
      },
      tooltip: {
        enable: true,
        textStyle: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
        }
      },
      title: "",
      currentInterval: 'MINUTE',
      lastRange: 0,
    };
  },
  provide: {
    chart: [SplineAreaSeries, Legend, DateTime, Highlight, Tooltip, Zoom, BarSeries, Category]
  },
  computed: {
    selectedStreamName() {
      const streamId = this.selectedStreamIds[0];
      const isLive = streamId.startsWith('live_');
      const stream = isLive ? this.availableAioStreams.find(x => x.key === streamId) : this.selectedVideoFiles.find(x => this.getKey(x) === streamId);
      const name = isLive ? stream.name : stream?.fileName;
      return name.slice(0, 10) + (name.length > 10 ? "..." : "");
    },
    isAnalyticsDashboardAllowed() {
      const validPacks = (this.subscriptions || [])?.filter((p) => {
        if (!p.enabled) return false;
        if (p.category === "live") return true;
        return [
          "5e8db3dcc3db0533322db725",
          "6362114709950a6a642540c3",
        ].includes(p.package?._id);
      });
      if (!validPacks.length) return false;

      return true;
    },
    ...mapGetters({
      loadingStatus: "Ui/loadingStatus",
      subscriptions: "User/subscriptions",
      availableAioStreams: "Streams/allInOneStreams",
      availableVodStreams: "Streams/vodStreams",
    }),
    optionMap() {
      return {
        play: "Plays",
        playAttempts: "Play Attempts",
        uniqueUsers: "Unique Users",
        concurrentViewers: "Concurrent Viewers",
        totalPageLoads: "Total Page Loads",
        totalHoursWatched: "Total Hours Watched",
        viewTime: "View Time",
        landscape: "Landscape",
        portrait: "Portrait",
      };
    },
    breakDownOptionMap() {
      return {
        all: "All Data",
        browser: "Browser",
        country: "Country",
        device_class: "Device",
        operatingSystem: "Operating System",
      };
    },
    suitableInterval() {
      let time = this.selectedMetricTimeFilter.time;
      if (this.selectedMetricTimeFilter === "custom") {
        time =
          (new Date(this.endTime).getTime() -
            new Date(this.startTime).getTime()) /
          (1000 * 60);
      }
      if (time > 1440) return "DAY";
      return time < 60 * 3 ? "MINUTE" : "HOUR";
    },
    countryToCountryCode() {
      return _.invert(this.countryList);
    }
  },
  watch: {},
  async mounted() {
    this.$store.dispatch("Ui/setLoadingStatus", true);
    await this.$store.dispatch("User/loadUser");

    if (this.$route.params.videoId) {
      if (this.$route.params.videoId.includes('.mp4')) {
        const vodMeta = await StreamService.getStreamPlaylistVideoMetaData(this.$route.params.videoId);
        this.selectedVideoFiles = [vodMeta];
      } else if (this.$route.params.videoId && this.$route.query.folderId) {
        const files = await StreamService.getStreamPlaylist(this.$route.query.folderId);
        const video = files.find(x => x.refId === this.$route.params.videoId);
        if (video) this.selectedVideoFiles = [video];
      } else {
        // live
        await this.$store.dispatch('Streams/getStreams');
      }
    }

    // initial loads
    await this.$store.dispatch("Ui/setLoadingStatus", false);

    if (this.$route.params.videoId) {
      const selectedStream = this.$route.params.videoId.replace('.mp4', '');
      if (this.$route.query.stream || this.$route.params.videoId?.startsWith('live_')) this.$refs.analyticsSelectStreamsModal.selectedStream = [selectedStream];
      else this.$refs.analyticsSelectStreamsModal.selectedVod = [selectedStream];
      this.selectedStreamIds = [selectedStream];
    }

    await this.$nextTick();
    const formatter = this.formatTime
    const formatXAxis = this.formatXAxis;
    const isResolutionOption = () => {
      return ['landscape', 'portrait'].includes(this.selectedOption)
    }
    try {

      // const chart = new Highcharts.Chart("chart-container", {
      //   chart: {
      //     type: "areaspline",
      //     // backgroundColor: "rgba(167, 181, 226, 0.08)",
      //     backgroundColor: null,
      //     zooming: {
      //       type: "x",
      //     },
      //   },
      //   credits: {
      //     enabled: false,
      //   },
      //   colors: [
      //     "rgba(32, 118, 255, 1)",
      //     "#FF530D",
      //     "#E82C0C",
      //     "#FF0000",
      //     "#E80C7A",
      //     "#E80C7A",
      //   ],
      //   title: false,
      //   legend: {
      //     enabled: false,
      //   },
      //   colorAxis: {
      //     gridLineColor: "red",
      //   },
      //   xAxis: {
      //     lineColor: '#fff',
      //     tickColor: '#fff',
      //     labels: {
      //       style: {
      //         color: "rgba(135, 148, 181, 1)",
      //         fontSize: 12,
      //         fontFamily: "Inter",
      //       },
      //     },
      //   },

      //   yAxis: {
      //     gridLineColor: "rgba(167, 181, 226, 0.08)",
      //     title: false,
      //     labels: {
      //       style: {
      //         color: "rgba(135, 148, 181, 1)",
      //         fontSize: 12,
      //         fontFamily: "Inter",
      //       },
      //     },
      //   },
      //   tooltip: {
      //     shared: true,
      //     formatter: function () {
      //         return this.points.reduce(function (s, point) {
      //             return isResolutionOption() ? formatter(point.y) : (s + '<br/>' + point.series.name + ': ' + formatter(point.y));
      //         }, '<b>' + formatXAxis(this.x) + '</b>');
      //     },
      //   },
      //   plotOptions: {
      //     series: {
      //       fillColor: {
      //         linearGradient: [0, 0, 0, 300],
      //         stops: [
      //           [0, "rgba(32, 118, 255, 1)"],
      //           [1, "rgba(0, 98, 255, 0)"],
      //         ],
      //       },
      //     },
      //   },

      //   series: [
      //     {
      //       name: "Total Impressions",
      //       data: [],
      //     },
      //   ],
      // });

      // this.chartRef = chart;
    } catch (e) {
      console.log(e);
    }

    await this.onSelectOption();
    const countryList = await IntegrationService.getCountryList();
    countryList.forEach((c) => {
      this.countryList[c.code.toUpperCase()] = c.name;
    });

    if (navigator.userAgent.match(/Android/i)
      || navigator.userAgent.match(/webOS/i)
      || navigator.userAgent.match(/iPhone/i)
      || navigator.userAgent.match(/iPad/i)
      || navigator.userAgent.match(/iPod/i)
      || navigator.userAgent.match(/BlackBerry/i)
      || navigator.userAgent.match(/Windows Phone/i)
    ) {
      // this.audienceDropdownExpanded = false;
      // this.$root.$emit('bv::toggle::collapse', 'audience')
    }
  },
  methods: {
    onTooltipRender(args) {
      console.log(args);
      
      // Extract the date from the arguments
      const dateValue = new Date(args.point.x);
      
      // Format the date (e.g., "Monday, August 23, 2024")
      const formattedDate = dateValue.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      
      // Set the tooltip text with the formatted date
      args.text = `${this.currentInterval !== 'DAY' ? (formattedDate + ' ') : ''}${args.text}`;
    },
    resetZoom() {
      this.onSelectOption();
    },
    onZoomComplete(args) {
      console.log(args);
      
      // detect resets
      if(args.currentZoomFactor !== args.previousZoomFactor && args.currentZoomFactor === 1 && args.currentZoomPosition === 0) {
        console.log('zoom reset');
        this.resetZoom();
        return;
      }

      const range = args.currentVisibleRange;
      if(Math.abs(args.currentZoomFactor - args.previousZoomFactor) < 0.00000000001) return;
      const visibleRange = range.delta;
      const mins = visibleRange / 60000;
      let interval = 'MINUTE';
      if(mins > 200 && mins < 1440*5) {
        interval = 'HOUR';
      } else if(mins >= 1440*5) {
        interval = 'DAY';
      }
      
      if(interval === this.currentInterval || this.lastRange === mins) return;
      console.log({mins, interval});
      this.currentInterval = interval;
      this.lastRange = mins;
      this.onSelectOption(null, false, interval, { startTime: new Date(Math.floor(range.min)).toISOString(), endTime: new Date(Math.ceil(range.max)).toISOString() }).then(() => {
        this.primaryXAxis.minimum = range.min;
        this.primaryXAxis.maximum = range.max;
        setTimeout(() => {
          const groupElement = document.getElementById('chart-container_Zooming_Reset');
          if (groupElement) {
            groupElement.style.opacity = 1;
            groupElement.setAttribute('opacity', '1');
            groupElement.removeEventListener('click', this.resetZoom);
            groupElement.addEventListener('click', this.resetZoom);
          }
        }, 200);
      });
    },
    toggleView(collapseName) {
      this.$refs['collapse-' + collapseName].toggleView()
      if (collapseName === 'resolution') this.resolutionDropdownExpanded = !this.resolutionDropdownExpanded
      if (collapseName === 'audience') this.audienceDropdownExpanded = !this.audienceDropdownExpanded
    },
    axisLabelRender(args) {
      const isTimeOption = ["totalHoursWatched", "viewTime"].includes(
        this.selectedOption
      );
      if (args.axis.propName === "primaryYAxis" && isTimeOption) {
        args.text = this.formatTime(args.value);
      }
    },
    formatXAxis(t) {
      if (['landscape', 'portrait'].includes(this.selectedOption)) return t;
      return moment(t).format('HH:mm DD/MM/YYYY');
    },
    parseTimeZone(time) {
      return new Date(time).toISOString();
    },
    onDateSelectionChanged() {
      setTimeout(() => (this.$refs.timeDropdown.visible = true));
    },
    onClickVideoDropdown() {
      this.$bvModal.show("modal-analytics-select-stream");
    },
    formatMsToHours(t) {
      const hrs = t / (3600 * 1000);
      return hrs.toFixed(2);
    },
    async fetchAndExportCsv(paramsArr) {
      this.showBreakdownLoader = true;
      const isAllBreakdown = this.selectedBreakdown === "all";
      const xField = isAllBreakdown ? 'Date' : this.breakDownOptionMap[this.selectedBreakdown];
      let results = {};
      let allOptions = [];
      for (const param of paramsArr) {
        const breakdownResult = isAllBreakdown ? await this.onSelectOption(param, true) : await this.onSelectBreakdown(this.selectedBreakdown, param);
        const map = Object.fromEntries(breakdownResult);
        allOptions.push(...Object.keys(map));
        results[param] = map;
      }

      if (isAllBreakdown) allOptions = allOptions.map(x => Number(x)).sort().reverse();

      allOptions = _.uniq(allOptions);

      let finalCsvObject = [];
      const paramKeys = _.keys(results);

      for (const option of allOptions) {
        const formattedOption = isAllBreakdown ? new Date(Number(option)).toISOString() : option;
        let row = { [xField]: formattedOption }

        paramKeys.forEach((key) => {
          const paramFormatted = this.optionMap[key];
          row[[paramFormatted]] = results[key][option] || 0;
          if (key === 'totalHoursWatched') row[[paramFormatted]] = Number((row[[paramFormatted]] / 3600000)?.toFixed(2));
          else if (key === 'viewTime') row[[paramFormatted]] = Number((row[[paramFormatted]] / 3600000)?.toFixed(2));
        });
        finalCsvObject.push(row);
      }

      csvDownload({
        data: finalCsvObject,
        filename: `breakdown_data_${this.selectedBreakdown === "all" ? 'All' : xField}`,
      });
      this.showBreakdownLoader = false;
    },
    exportCsv() {
      const isAll = this.selectedBreakdown === "all";
      const field1 = isAll
        ? "Date"
        : this.breakDownOptionMap[this.selectedBreakdown];
      const field2 = this.optionMap[this.selectedOption];

      let rawData = this.breakdownData;
      if (isAll)
        rawData = this.allData.map((x) => [new Date(x[0]).toISOString(), x[1]]);

      const data = rawData.map((d) => ({ [field1]: d[0], [field2]: d[1] }));
      csvDownload({
        data,
        filename: `breakdown_data_${field1.toLowerCase()}_${field2.toLowerCase()}`,
      });
    },
    refreshData() {
      this.onSelectOption();
    },
    onSelectDateTimeRange(dateTimeRange) {
      this.selectedMetricTimeFilter = dateTimeRange;
      this.$nextTick(() => {
        this.refreshData();
      });
    },
    triggerMetricDateSelection(widgetId) {
      this.maxPossibleDate = new Date().toISOString();
      document.getElementById(widgetId).focus();
    },
    async onSelectOption(option, justFetchData = false, customInterval = null, customRange = null) {
      const formatTime = this.formatTime;
      if (!option) option = this.selectedOption;

      if (!justFetchData && !customInterval) {
        this.showLocalLoader = true;
        this.selectedOption = option;

        this.selectedBreakdown = "all";
      }

      let startTime =
        this.selectedMetricTimeFilter === "custom"
          ? this.parseTimeZone(this.startTime)
          : new Date(
            Date.now() - this.selectedMetricTimeFilter.time * 60 * 1000
          ).toISOString();
      let endTime =
        this.selectedMetricTimeFilter === "custom"
          ? this.parseTimeZone(this.endTime)
          : new Date().toISOString();

      let tempInterval = customInterval || this.suitableInterval;
      if (tempInterval === 'HOUR' && !customInterval) tempInterval = 'MINUTE';
      if (tempInterval === 'DAY' && !customInterval) tempInterval = 'HOUR';

      if(customRange) {
        startTime = customRange.startTime;
        endTime = customRange.endTime;
      }

      let data = await MetricsService.getBitmovinAnalytics({
        startTime,
        endTime,
        type: option,
        interval: tempInterval,
        selectedStreams: this.selectedStreamIds,
      });

      if (data?.length >= 199 || !data?.length) {
        if(tempInterval === 'MINUTE') tempInterval = 'HOUR';
        else if(tempInterval === 'HOUR') tempInterval = 'DAY';
        data = await MetricsService.getBitmovinAnalytics({
          startTime,
          endTime,
          type: option,
          interval: tempInterval,
          selectedStreams: this.selectedStreamIds,
        });
      }
      console.log(JSON.stringify(data));
      
      if(customRange && !data.length) return [];

      this.currentInterval = tempInterval;
      console.log(tempInterval);
      
      this.primaryXAxis.minimum = startTime;
      this.primaryXAxis.maximum = endTime;

      const isTimeOption = ["totalHoursWatched", "viewTime"].includes(
        this.selectedOption
      );

      if (isTimeOption) {
        data = data.map((x) => [x[0], x[1]]);
      }

      if (justFetchData) return data;
      
      console.log("deleting series from chart");
      this.deleteSeriesFromChart();
      console.log("DONE: deleted series from chart");

      
      this.allData = data;
      this.allDataReversed = data.reverse();
      
      this.categoryData = [];
      if (["landscape", "portrait"].includes(this.selectedOption)) {
        this.categoryData = data.map((x) => [`${x[0]} x ${x[1]}`, x[2]])
      }

      if (isTimeOption) {
        this.primaryYAxis.labelFormat = '{value} H'
      } else {
        this.primaryYAxis.labelFormat = '{value}';
      }

      // this.chartRef.redraw();
      this.showLocalLoader = false;
      const matchMedia = window.matchMedia('(max-width: 60px)');
      if (navigator.userAgent.match(/Android/i)
        || navigator.userAgent.match(/webOS/i)
        || navigator.userAgent.match(/iPhone/i)
        || navigator.userAgent.match(/iPad/i)
        || navigator.userAgent.match(/iPod/i)
        || navigator.userAgent.match(/BlackBerry/i)
        || navigator.userAgent.match(/Windows Phone/i)
        || matchMedia.matches
      ) {
        // this.audienceDropdownExpanded = false;
        // this.$root.$emit('bv::toggle::collapse', 'audience')
      }

      setTimeout(() => {
        this.chartRefreshCounter++;
        console.log("DONE: chartRefreshCounter");
      });
    },
    deleteSeriesFromChart() {
      // const chart = this.chartRef;
      // while (chart.series.length) {
      //   chart.series[0].remove();
      // }
    },
    async onSelectBreakdown(option, type = null) {
      if (!type) this.breakdownShow = true;
      let result = [];
      if (option === "all") return result;

      if (!type) {
        this.selectedBreakdownOptions = [];
        this.onSelectBreakdownOption(["total"]);
        this.showBreakdownLoader = true;
        this.breakdownData = [];
        this.cachedBreakdownOptionData = {};
      }

      const startTime =
        this.selectedMetricTimeFilter === "custom"
          ? this.parseTimeZone(this.startTime)
          : new Date(
            Date.now() - this.selectedMetricTimeFilter.time * 60 * 1000
          ).toISOString();
      const endTime =
        this.selectedMetricTimeFilter === "custom"
          ? this.parseTimeZone(this.endTime)
          : new Date().toISOString();

      if (this.selectedOption) {
        if (!type) {
          this.totalBreakDownValue = 0;
          const total = await MetricsService.getBitmovinAnalytics({
            startTime,
            endTime,
            type: type || this.selectedOption,
            groupBy: undefined,
            selectedStreams: this.selectedStreamIds,
            interval: undefined,
          });
          if (total.length) {
            this.totalBreakDownValue = total[0][0];
          }
        }

        let breakDownData = await MetricsService.getBitmovinAnalytics({
          startTime,
          endTime,
          type: type || this.selectedOption,
          groupBy: this.selectedBreakdown.toUpperCase(),
          selectedStreams: this.selectedStreamIds,
          interval: undefined,
        });

        breakDownData.forEach((x, i) => {
          breakDownData[i][0] = this.selectedBreakdown === "country" ? (this.countryList[x[0]] || 'Other') : (x[0] === '?' ? 'Other' : x[0]);
        })
        result = _.orderBy(breakDownData, [(x) => x[1]], ["desc"]);
        if (!type) this.breakdownData = result;
      }

      if (!type) this.showBreakdownLoader = false;
      return result;
    },
    async fetchBreakdownDataCached(option) {
      if (option in this.cachedBreakdownOptionData) return this.cachedBreakdownOptionData[option];
      const startTime =
        this.selectedMetricTimeFilter === "custom"
          ? this.parseTimeZone(this.startTime)
          : new Date(
            Date.now() - this.selectedMetricTimeFilter.time * 60 * 1000
          ).toISOString();
      const endTime =
        this.selectedMetricTimeFilter === "custom"
          ? this.parseTimeZone(this.endTime)
          : new Date().toISOString();

      let tempInterval = this.suitableInterval;
      if (tempInterval === 'HOUR') tempInterval = 'MINUTE';
      let data = await MetricsService.getBitmovinAnalytics({
        startTime,
        endTime,
        type: this.selectedOption,
        selectedStreams: this.selectedStreamIds,
        interval: tempInterval,
        filter: [{
          name: this.selectedBreakdown.toUpperCase(),
          operator: 'EQ',
          value: this.countryToCountryCode[option],
        }]
      });

      if (data?.length >= 199 || !data?.length) {
        tempInterval = 'HOUR';
        data = await MetricsService.getBitmovinAnalytics({
          startTime,
          endTime,
          type: this.selectedOption,
          selectedStreams: this.selectedStreamIds,
          interval: tempInterval,
          filter: [{
            name: this.selectedBreakdown.toUpperCase(),
            operator: 'EQ',
            value: this.countryToCountryCode[option],
          }]
        });
      }

      this.cachedBreakdownOptionData[option] = data;
      return this.cachedBreakdownOptionData[option];
    },
    async onSelectBreakdownOption(options) {
      options = _.uniq(options);
      if (options.length > 5) return;
      this.deleteSeriesFromChart();
      if (options.includes("total")) {
        // chart.addSeries({
        //   name: "All",
        //   type: "areaspline",
        //   data: this.allData,
        // });
      }

      options = options.filter((x) => x !== "total");

      const finalData = await Promise.all(options.map(async (option) => {
        let tsData = await this.fetchBreakdownDataCached(option);
        tsData = _.orderBy(tsData, [(x) => x[0]], ["asc"]);
        return [option, tsData];
      }));

      if (finalData.length) {
        while (chart.series.length) {
          chart.series[0].remove();
        }
      }
      finalData.forEach(x => {
        chart.addSeries({
          name: x[0],
          type: "areaspline",
          data: x[1],
        });
      })
    },
    addStream(streams, vods, selectedVideoFiles) {
      this.selectedStreamIds = [...streams, ...vods];
      this.selectedVideoFiles = selectedVideoFiles?.slice() ?? [];
    },
    getKey(stream) {
      return stream?.refId
        ? stream?.refId
        : stream.id.replace(".mp4", "");
    },
    getDate(d) {
      if (this.suitableInterval === "DAY") {
        return moment(d).format("MMMM DD, YYYY");
      }
      return moment(d).format("h:mm A");
    },
    formatTime(ms) {
      const durationSeconds = ms / 1000;
      if (this.selectedOption === "viewTime") {
        if (!durationSeconds) return "-";
        let formatStr = "s's'";
        if (durationSeconds >= 60) formatStr = "m'm' s's'";
        if (durationSeconds >= 3600) formatStr = "h'h' m'm'";
        if (durationSeconds >= 1440 * 60) formatStr = "d'd' h'h'";
        return Duration.fromObject({ seconds: durationSeconds }).toFormat(
          formatStr
        );
      } else if (this.selectedOption === "totalHoursWatched") {
        return Number((durationSeconds / 3600).toFixed(2)) + " h";
      }

      return Number(ms).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });;
    },
    formatValue(value) {
      return ["totalHoursWatched", "viewTime"].includes(this.selectedOption)
        ? `${value.toFixed(2)} h`
        : value;
    },
    formatValueNumber(value) {
      return ["totalHoursWatched", "viewTime"].includes(this.selectedOption)
        ? Number(this.formatMsToHours(value))
        : value;
    },
    changeBreakdown() {
      this.breakdownShow = false;
    }
  },
};
</script>

<style scoped>
.page-content-area {
  box-sizing: border-box;
}

.content-area {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 80%;
  min-height: 100vh;
  height: max-content;
}

.main-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.side-bar {
  max-width: 20%;
}

#chart-container {
  min-height: 600px;
}

#chartContainer {
  height: 100%;
  width: 100%;
}

.chart-container {
  width: 100%;
  min-height: 600px;
  background: rgba(167, 181, 226, 0.08);
}

.btn-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 4px 15px 4px 0;
}

.dropdown-label {
  color: var(--c-dark-8);
  font-size: var(--font-s-xs);
}

.selected-item {
  color: #ffffff;
  font-size: var(--font-s-s);
}

.transform-none {
  transform: none !important;
}

.collapse-button {
  font-size: var(--font-s-xl);
  position: relative;
  display: flex;
  text-decoration: none;
}

.collapsible-item-container.collapsing,
.collapsible-item-container.show {
  display: flex !important;
  width: 100%;
  flex-direction: column;
}

.collapsible-item-container button {
  text-decoration: none;
  display: flex;
  justify-content: flex-start;
  color: var(--c-dark-8);
  margin-left: 16px;
}

.collapsible-item-container button svg {
  margin-right: 5px;
}

.collapsible-item-container button:hover,
.selected-option {
  background: rgba(167, 181, 226, 0.08);
  color: #ffffff;
}

.local-loader {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center !important;
  height: 50vh;
}

.local-breakdown-loader {
  width: 80%;
  display: flex;
  justify-content: center;
  align-content: center;
  height: 100%;
}

.break-down-table {
  margin-left: 0;
  margin-right: 0;
  display: flex;
  color: var(--c-dark-9);
  margin-top: 5px;
  margin-bottom: 5px;
  border-top: 1px solid rgba(167, 181, 226, 0.08);
  background: rgba(167, 181, 226, 0.08);
}

.break-down-filters {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.break-down-filters h3 {
  border-bottom: 1px solid rgba(167, 181, 226, 0.08);
  text-align: left;
  width: 100%;
  padding: 4px;
}

.breakdown-options {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.break-down-results {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  border-left: 1px solid rgba(167, 181, 226, 0.08);
  font-size: var(--font-s-s);
  color: var(--c-dark-9);
}

.break-down-results .title {
  border-bottom: 1px solid rgba(167, 181, 226, 0.08);
  display: flex;
  justify-content: space-between;
  padding-left: 8px;
}

.break-down-results-header {
  font-family: "Inter";
  color: var(--c-dark-7);
  font-size: var(--font-s-xs);
  padding: 8px 0;
  border-bottom: 1px solid rgba(167, 181, 226, 0.08);
}

.export-csv-btn {
  font-family: "Inter";
  color: var(--c-dark-8);
  font-size: 13px;
}

.no-data {
  min-height: 500px;
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-family: "Inter";
}

.main-err-msg {
  font-size: var(--font-s-s);
  font-weight: 500;
  color: #ffffff;
}

.secondary-err-msg {
  font-size: 13px;
  font-weight: 400;
  color: var(--c-dark-7);
}

.selected-option-header {
  font-size: var(--font-s-xxl);
  font-weight: 500;
  text-align: left;
  padding: 8px 0;
  padding-left: 8px;
}

.tooltip-container {
  max-width: 750px !important;
}

:deep(.tooltip-container .tooltip-inner) {
  max-width: none;
}

.tooltip-content {
  text-align: left;
  padding: 5px;
}

.not-available {
  color: rgba(209, 219, 240, 1);
  font-family: "Inter";
  font-weight: 500;
  font-size: var(--font-s-m);
}

.res-block {
  display: none;
}

@media only screen and (max-width:600px) {
  .res-block {
    display: inline-block;
  }

  .btn-refresh {
    position: absolute;
    top: 118px;
    right: 10px;
  }

  .global-filters {
    margin-bottom: 24px;
  }

  .global-filters>div {
    width: 100%;
  }


  .global-filters .filters {
    justify-content: space-between !important;
    display: flex;
    width: 100%;
  }

  /* .side-bar>button:first-child {
  opacity: 0;
  position: absolute;
  top:0;
  left: 0;
  z-index: 999;
  width: 100%;
  left: 0;
} */
  .side-bar {
    width: 100%;
    max-width: 100%;
    position: relative;
    overflow: hidden;
  }

  .global-filters+.main-content {
    flex-direction: column;
  }

  /* .side-bar>button {
    display: none !important;
} */
  .not-collapsed .side-bar>div>button {
    display: flex;
  }

  .not-collapsed+div>button {
    margin: 0 !important;
    display: none;
    width: 100%;
  }

  .not-collapsed+div>button.selected-option {
    display: flex;
  }

  #audience {
    /* height: 33px !important; */
    display: flex !important;
    width: 100%;
    flex-wrap: wrap;
  }

  .collapsed+#audience {
    height: auto !important;
  }

  /* .global-filters+.main-content>div:not(.resClass) .collapsed+#audience {
  height: 33px !important;
    display: flex !important;
    width: 100%;
    flex-wrap: wrap;
} */
  .collapsible-item-container button {
    width: 100%;
    margin: 0;
  }

  .main-content .content-area {
    width: 100%;
  }

  .break-down-results {
    display: none;
  }

  .break-down-filters {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
  }

  .show-level .break-down-filters {
    display: none;
  }

  .show-level .break-down-results {
    display: block;
  }

  .break-down-results h3 {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
  }

  .break-down-table {
    flex-wrap: wrap;
  }

  .side-bar>button.collapse-button.btn-link.collapsed {
    display: none;
  }

  /* .side-bar:after {
    content: "";
    border-top: 0.3em solid;
    border-right: 0.3em solid transparent;
    border-bottom: 0;
    border-left: 0.3em solid transparent;
    position: absolute;
    top: 15px;
    right: 10px;
} */

  .tooltip-container {
    width: 90%;
    max-width: 100% !important;
    left: 5% !important;
    transform: translate3d(0px, 388px, 0px) !important;
  }

  .global-filters .dropdown {
    margin: 0 !important;
    width: 49%;
  }

  #resolution {
    display: block !important;
    margin-bottom: 15px;
  }

  .show~#resolution {
    position: absolute;
    display: flex !important;
    top: 0;
    left: 0;
    width: 100%;
  }

  .show~#resolution .btn-link.selected-option {
    position: absolute;
    top: 0;
    left: 0;
  }

  .show~#resolution>button {
    display: none;
  }

  .show~#resolution>button.selected-option {
    display: flex;
  }
}
</style>

<style>
.datetime-input-hidden {
  position: absolute;
  background: red;
  z-index: -1;
}

.date-time-picker-input {
  padding-left: 0;
  font-size: smaller;
  border: 1px solid #474e67 !important;
}
</style>
